/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component} from "react";

import {observer} from "mobx-react";

import {Project} from "../../models/project";
import {Icon} from "../../core/components/icon/icon";
import {Button, Checkbox, Dimmer, Form, Input, Loader, Message, Progress, Select, TextArea} from "semantic-ui-react";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";

import "./project-form.scss";
import {AppState} from "../../components/app/app-mobx";
import {DropdownItemProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem";
import {Contractor} from "../../models/contractor";
import {InputOnChangeData} from "semantic-ui-react/dist/commonjs/elements/Input/Input";
import {Municipality} from "../../models/municipality";
import {ProjectMunicipality} from "../../models/project-municipality";
import {ProjectIdHints} from "./project-id-hints";

export enum StatusType {
    Saving = "saving",
    Warning = "warning",
    Complete = "successful",
    Error = "error"
}

export interface ProjectFormProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    appStore: AppState;
    auftragsverwaltung: Auftragsverwaltung;
    project: Project | null;

    onCancel?: (e: React.MouseEvent) => void;
    onSave?: (project: Project) => void;
}

@observer
export class ProjectForm extends Component<ProjectFormProps, any> {

    autorunDisposers: any[] = [];

    constructor(props: ProjectFormProps) {
        super(props);
        this.state = {
            saveError: null,
            saveState: null,
            projectTypeOptions: null, // available project types,
            municipalityOptions: null, // municipalities for select box
            contractorOptions: null, // available contractors,
            municipalities: null, // municipalities for lookups
            filteredProjectIds: [], // ids containing the ID specified in ID input field
            projectIdMatch: null,  // an existing project ID matching the ID in input field

            // pass project properties to state. Use controlled state with form controls
            projectTypeId: this.props.project?.typeId,
            projectId: this.props.project?.id,
            projectUuid: this.props.project?.uuid,
            projectName: this.props.project?.name || "",
            projectReason: this.props.project?.reason || "",
            projectContractorId: this.props.project?.contractorId,
            projectMunicipalities: [],
            projectMunicipalityIds: [],
            projectMunicipalityIdsInDB: [], // remember ids stored in DB as long as ids are not saved
            projectGkoa: this.props.project?.gkoa || false,
            projectGhkw: this.props.project?.ghkw || false,
            projectTypeIdError: false,
            projectIdError: false,
            projectUuidError: false,
            projectNameError: false,
            projectContractorIdError: false,
            projectMunicipalityIdsError: false
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    componentDidMount(): void {
        this.loadDomains();
        this.loadProjectMunicipalities();
    }

    componentWillUnmount(): void {
        /*
         * Avoid `Warning: Can't perform a React state update on an unmounted component. This is a no-op, but it
         * indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks
         * in the componentWillUnmount method.`
         */
        this.setState({});
    }

    render(): React.ReactElement | null {
        const {view, project, auftragsverwaltung} = this.props;
        const {
            contractorOptions,
            municipalityOptions,
            projectTypeOptions,
            saveError,
            saveState,
            projectTypeId,
            projectId,
            projectUuid,
            projectName,
            projectReason,
            projectContractorId,
            projectMunicipalityIds,
            projectGkoa,
            projectGhkw,
            projectTypeIdError,
            projectIdError,
            projectUuidError,
            projectNameError,
            projectContractorIdError,
            projectMunicipalityIdsError,
            filteredProjectIds,
            projectIdMatch
        } = this.state;
        // const {graphic} = this.state;
        if (!view) {
            return null;
        }
        if (!project || !project.id) {
            return null;
        }

        if (!contractorOptions || (this.isGefahrenkarte && !projectTypeOptions)) {
            return (
                <div>
                    <Dimmer active inverted>
                        <Loader inverted content="Formulardaten laden" />
                    </Dimmer>
                </div>
            );
        }

        let progress: React.ReactElement | null = null;
        if (saveState) {
            progress = (
                <Progress
                    percent={saveState === StatusType.Complete ? 100 : 50}
                    attached="bottom"
                    active={saveState === StatusType.Saving}
                    warning={saveState === StatusType.Warning}
                    error={saveState === StatusType.Error}
                    success={saveState === StatusType.Complete}
                />
            );
        }

        return (
            <div className="project-form">
                <ProjectIdHints
                    auftragsverwaltung={auftragsverwaltung}
                    filteredProjectIds={filteredProjectIds}
                    projectIdMatch={projectIdMatch} />
                <Form size="huge" onSubmit={() => this.handleSave(project)}>
                    {projectTypeOptions &&
                        <Form.Field
                            name="projectTypeId"
                            control={Select}
                            options={projectTypeOptions}
                            label={{ children: "Projekttyp", htmlFor: "form-select-control-project-type" }}
                            placeholder={"Projekttyp"}
                            required
                            search
                            searchInput={{ id: "form-select-control-project-type" }}
                            value={projectTypeId}
                            readOnly={this.isRisikouebersicht} // type is fix for Risikoübersichten
                            onChange={this.handleChange}
                            error={projectTypeIdError?
                                {
                                    content: "Bitte einen Projekttyp wählen",
                                }:null
                            }
                        />
                    }
                    <Form.Group widths="equal">
                        {!this.isRisikouebersicht &&
                            <Form.Field
                                id="form-input-control-project-id"
                                name="projectId"
                                control={Input}
                                label={{ children: "Projektnummer", htmlFor: "form-input-control-project-id" }}
                                placeholder="z.B. 4402"
                                required
                                width={4}
                                value={projectId}
                                readOnly={!project.isNewRecord}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                                    this.loadProjectIdSuggestions(data.value);
                                    this.handleChange(event, data);
                                }}
                                error={projectIdError?
                                    {
                                        content: "Bitte eine gültige ID eingeben",
                                    }:null
                                }
                            />
                        }
                        <Form.Field
                            id="form-input-control-project-uuid"
                            name="projectUuid"
                            control={Input}
                            label={{ children: "Projektnummer vif", htmlFor: "form-input-control-project-uuid" }}
                            placeholder="z.B. 10816.4"
                            required={this.isRisikouebersicht}
                            width={4}
                            value={projectUuid}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                                this.handleChange(event, data);
                            }}
                            error={projectUuidError?
                                {
                                    content: "Bitte eine gültige ID eingeben",
                                }:null
                            }
                        />
                    </Form.Group>
                    <Form.Field
                        id="form-input-control-project-name"
                        name="projectName"
                        control={Input}
                        label={{ children: "Projektname", htmlFor: "form-select-input-project-name" }}
                        placeholder="z.B. GK Pfaffnau"
                        required
                        value={projectName}
                        onChange={this.handleChange}
                        error={projectNameError?
                            {
                                content: "Bitte einen Namen eingeben",
                            }:null
                        }
                    />
                    <Form.Field
                        id="form-textarea-control-project-reason"
                        name="projectReason"
                        control={TextArea}
                        label={{ children: "Projektgrund", htmlFor: "form-select-control-project-reason" }}
                        placeholder="z.B. Überarbeitung Wasserprozesse"
                        value={projectReason}
                        onChange={this.handleChange}
                    />
                    {contractorOptions &&
                        <Form.Field
                            name="projectContractorId"
                            control={Select}
                            options={contractorOptions}
                            label={{ children: "Auftragnehmer", htmlFor: "form-select-control-project-contractor" }}
                            placeholder="Auftragnehmer"
                            required
                            search
                            searchInput={{ id: "form-select-control-project-contractor" }}
                            value={projectContractorId}
                            onChange={this.handleChange}
                            error={projectContractorIdError?
                                {
                                    content: "Bitte einen Auftragnehmer auswählen",
                                }:null
                            }
                        />
                    }
                    {municipalityOptions &&
                        <Form.Field
                            name="projectMunicipalityIds"
                            control={Select}
                            multiple
                            options={municipalityOptions}
                            label={{ children: "Gemeinden", htmlFor: "form-select-control-project-municipalities" }}
                            placeholder="Gemeinden"
                            required
                            search
                            searchInput={{ id: "form-select-control-project-municipalities" }}
                            value={projectMunicipalityIds}
                            onChange={this.handleChange}
                            closeOnChange
                            error={projectMunicipalityIdsError?
                                {
                                    content: "Bitte mind. eine Gemeinde auswählen",
                                }:null
                            }
                        />
                    }
                    {this.isRisikouebersicht &&
                        <Form.Field
                            id="form-checkbox-control-project-gkoa"
                            name="projectGkoa"
                            control={Checkbox}
                            label={{
                                children: "GFK Oberflächenabfluss",
                                htmlFor: "form-checkbox-control-project-gkoa"
                            }}
                            checked={projectGkoa}
                            onChange={this.handleChange}
                        />
                    }
                    {this.isRisikouebersicht &&
                        <Form.Field
                            id="form-checkbox-control-project-ghkw"
                            name="projectGhkw"
                            control={Checkbox}
                            label={{
                                children: "GHK Wasser",
                                htmlFor: "form-checkbox-control-project-ghkw"
                            }}
                            checked={projectGhkw}
                            onChange={this.handleChange}
                        />
                    }
                    {progress}
                    {saveState && saveState === StatusType.Error &&
                        <Message attached="bottom" negative>
                            <Icon name={"error"} />
                            Fehler beim Speichern! {saveError}
                        </Message>
                    }
                    <Button className={"save"} primary type="submit">
                        <Icon name={"floppy_disk"} />
                        Speichern
                    </Button>

                    <Button className={"cancel"} secondary
                        onClick={(e: React.MouseEvent) => {
                            if (this.props.onCancel) {
                                this.props.onCancel(e);
                            }
                        }}>
                        <Icon name={"navigate_cross"} />
                        Abbrechen
                    </Button>
                </Form>
            </div>
        );
    }

    private get isGefahrenkarte(): boolean {
        const {appStore} = this.props;
        return appStore.topic?.id === "projectsGk";
    }
    private get isRisikouebersicht(): boolean {
        const {appStore} = this.props;
        return appStore.topic?.id === "projectsR";
    }

    private handleChange(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void {
        let value: any;
        if (data.type === "checkbox") {
            value = data.checked;
        }
        else {
            value = data.value;
        }
        this.setState({[data.name]: value});
    };

    private handleSave(project: Project): void {
        const {appStore, auftragsverwaltung, onSave} = this.props;
        const {
            projectTypeId,
            projectId,
            projectUuid,
            projectName,
            projectReason,
            projectContractorId,
            projectGkoa,
            projectGhkw
        } = this.state;

        if (!project) {
            throw new Error("Project undefined saving project");
        }

        const hasError = this.validateFormInput();
        if (hasError) {
            this.setState({formError: true});
            return;
        }

        // make progress indicator visible
        this.setState({
            formError: false,
            saveState: StatusType.Saving
        });

        // autoincrement project id for risk projects
        this.autoIncrementProjectId(project).then((id) => {

            const updated = {
                typeId: projectTypeId,
                id: id || parseInt(projectId, 10), // use ID from form for gk projects
                uuid: projectUuid,
                name: projectName,
                reason: projectReason,
                contractorId: projectContractorId,
                gkoa: projectGkoa ? 1 : 0,
                ghkw: projectGhkw ? 1 : 0,
            };

            // change project ID to final ID
            if (project) {
                project.id = updated.id;
            }

            // save municipalities first
            this.saveProjectMunicipalities(project).then(() => {

                // then save project
                const user = appStore.esriCredential?.userId || "";
                const type = appStore.topic?.id === "projectsGk" ? "gefahrenkarte" : "risikoübersicht";
                auftragsverwaltung.ProjectStore?.save(project, type, updated, user)
                    .then((savedProject) => {
                        if (savedProject) {
                            appStore.setObjectId(updated.id.toString());

                            this.setState({
                                saveState: StatusType.Complete,
                                saveError: null
                            });
                            if (onSave) {
                                onSave(project);
                            }
                            auftragsverwaltung.setStatus("successful");
                            auftragsverwaltung.setEditing(false);
                        }
                    }).catch((error) => {
                        this.setState({
                            saveState: StatusType.Error,
                            saveError: error.message
                        });
                    });
            });
        });
    }

    private autoIncrementProjectId(project: Project): Promise<number | null> {
        const {auftragsverwaltung} = this.props;

        return new Promise((resolve) => {
            if (!project.isNewRecord || this.isGefahrenkarte) {
                // do not auto increment. use existing ID or ID from form
                resolve(null);
            }

            // create autoincrement project ID for risk projects
            if (this.isRisikouebersicht) {
                const currentYear = new Date().getFullYear();
                auftragsverwaltung.ProjectStore.maxProjectId(currentYear).then((id) => {
                    if (!id) {
                        // start ID not used yet
                        resolve(parseInt(`${currentYear}001`, 10));
                        return;
                    }
                    // increment
                    resolve(id + 1);
                    return;
                });
            }
        });
    }

    /**
     * Load related municipalities
     * @private
     */
    private loadProjectMunicipalities() {
        const {project, auftragsverwaltung} = this.props;

        if (!project?.id) {
            return;
        }

        auftragsverwaltung.ProjectMunicipalityStore.loadItems([project.id])
            .then((projectMunicipalities: ProjectMunicipality[]) => {
                const projectMunicipalityIds: number[] = projectMunicipalities.map((pm: ProjectMunicipality) => {
                    return pm.bfsNr;
                });
                this.setState({
                    projectMunicipalities,
                    projectMunicipalityIds,
                    // remember ids in db as long as ids are not saved
                    projectMunicipalityIdsInDB: [...projectMunicipalityIds] // clone
                });
            });
    }

    /**
     * Save municipalities related to the project
     */
    private saveProjectMunicipalities(project: Project): Promise<void> {
        return new Promise((resolve, reject) => {
            const {appStore, auftragsverwaltung} = this.props;
            const {projectId, projectMunicipalities, projectMunicipalityIdsInDB, projectMunicipalityIds} = this.state;

            if (!project) {
                throw new Error("Project undefined saving project");
            }

            const before = projectMunicipalityIdsInDB;
            const selected = projectMunicipalityIds;

            // all selected that are not included before. Filter all that are already present before
            const idsToAdd: number[] = selected.filter((bfsNr: number) => !before.includes(bfsNr));
            // all present before but not included in selection anymore. Filter all that are not selected anymore
            const idsToDelete: number[] = before.filter((bfsNr: number) => !selected.includes(bfsNr));

            const municipalitiesToAdd = idsToAdd.map((bfsNr) => {
                return new ProjectMunicipality({
                    projectId: parseInt(projectId, 10),
                    bfsNr
                });
            });
            // pass OBJECTIDs of present project municipality to delete (not new features)
            const municipalitiesToDelete: number[] = idsToDelete.map((bfsNr) => {
                return projectMunicipalities.find((pm: ProjectMunicipality) => pm.bfsNr === bfsNr);
            });

            const user = appStore.esriCredential?.userId || "";
            auftragsverwaltung.ProjectMunicipalityStore.save(project, municipalitiesToAdd, municipalitiesToDelete, user)
                .then(() => {
                    resolve();
                }).catch((pmError) => {
                    reject();
                    this.setState({
                        saveState: StatusType.Error,
                        saveError: pmError.message
                    });
                });
        });
    }

    /**
     * Load domains and sources for select boxes.
     *
     * @private
     */
    private loadDomains() {
        const {appStore, auftragsverwaltung} = this.props;

        const isGefahrenkarte = appStore.topic?.id === "projectsGk";
        const isRisikouebersicht = appStore.topic?.id === "projectsR";

        // load project types
        auftragsverwaltung.loadDomain("project", "typeId").then((projectTypeDomain) => {
            const projectTypeOptions: DropdownItemProps[] = [];
            let riskOverviewOptionId = -1;

            projectTypeDomain?.codedValues.forEach((codedValue: __esri.CodedValue) => {
                // TODO: DIRTY. Make filter configurable
                if (codedValue.name === "Risikoübersicht") {
                    riskOverviewOptionId = (typeof codedValue.code === "string" ? parseInt(codedValue.code, 10) : codedValue.code);
                    if (isGefahrenkarte) {
                        // do not include this option for Gefahrenkarte projects
                        return;
                    }
                } else {
                    if (isRisikouebersicht) {
                        // do not include this option for Risikoübersicht projects
                        return;
                    }
                }
                projectTypeOptions.push({
                    key: codedValue.code,
                    text: codedValue.name,
                    value: codedValue.code
                });
            });

            const state = {
                projectTypeOptions
            };
            if (isRisikouebersicht) {
                // set type fix to 'Risikoübersicht'
                state["projectTypeId"] = riskOverviewOptionId;
            }
            this.setState(state);
        });

        const contractorOptions: DropdownItemProps[] = [];
        auftragsverwaltung.ContractorStore.loadItems().then((contractors: Contractor[]) => {
            contractors.sort(Contractor.sortByOrganisation).forEach((contractor: Contractor) => {
                contractorOptions.push({
                    key: contractor.id,
                    text: `${contractor.organisation}, ${contractor.city}`,
                    value: contractor.id
                });
            });
            this.setState({
                contractorOptions
            });
        });

        const municipalities = [...auftragsverwaltung.MunicipalityStore.items.values()];
        // municipalities are multipart features, so we expect to get multiple entries per municipality
        // --> make unique for select options
        const unique =  [...new Map(municipalities.map((m) => [m.name, m])).values()];
        const sorted = unique.sort(Municipality.sortByName);

        const municipalityOptions = sorted.map((municipality: Municipality) => {
            return {
                key: municipality.bfsNr,
                text: municipality.name,
                value: municipality.bfsNr
            };
        });
        this.setState({
            municipalities: sorted,
            municipalityOptions
        });
    }

    private loadProjectIdSuggestions(projectIdInput: string): void {
        const {auftragsverwaltung} = this.props;

        if (!projectIdInput || projectIdInput.length === 0) {
            return;
        }

        // first get municipalities with full information
        auftragsverwaltung.ProjectStore.requestIds(projectIdInput).then((ids) => {
            // display form error when id is already in use
            const idInputAsNumber = parseInt(projectIdInput, 10);
            const alreadyInUse = ids?.includes(idInputAsNumber);
            let match: number | undefined;
            if (alreadyInUse) {
                match = ids?.filter((id: number) => id === idInputAsNumber)[0];
            }
            this.setState({
                filteredProjectIds: ids,
                projectIdMatch: match,
                formError: alreadyInUse,
                projectIdError: alreadyInUse
            });
        }).catch((error) => {
            this.setState({
                error
            });
        });
    }

    private validateFormInput(): boolean {
        const {
            projectTypeId,
            projectId,
            projectUuid,
            projectName,
            projectContractorId,
            projectMunicipalityIds
        } = this.state;

        const state: any = {};
        let hasErrors = false;
        if (!projectTypeId || projectTypeId === "") {
            state["projectTypeIdError"] = true;
            hasErrors = true;
        } else {
            state["projectTypeIdError"] = false;
        }
        if (!projectId || projectId === "") {
            state["projectIdError"] = true;
            hasErrors = true;
        } else {
            state["projectIdError"] = false;
        }
        if (this.isRisikouebersicht && !projectUuid || projectUuid === "") {
            state["projectUuidError"] = true;
            hasErrors = true;
        } else {
            state["projectUuidError"] = false;
        }
        if (!projectName || projectName === "") {
            state["projectNameError"] = true;
            hasErrors = true;
        } else {
            state["projectNameError"] = false;
        }
        if (!projectContractorId || projectContractorId === "") {
            state["projectContractorIdError"] = true;
            hasErrors = true;
        } else {
            state["projectContractorIdError"] = false;
        }
        if (!projectMunicipalityIds || projectMunicipalityIds.length === 0) {
            state["projectMunicipalityIdsError"] = true;
            hasErrors = true;
        } else {
            state["projectMunicipalityIdsError"] = false;
        }
        this.setState(state);
        return hasErrors;
    }
}
